import {
  MAX_ZOOM,
  MIN_ZOOM,
  type Exaggeration,
  type ViewState,
} from './mapDisplay';

/** Ground extent in northing/easting. */
export interface Bounds {
  minN: number;
  maxN: number;
  minE: number;
  maxE: number;
}

export interface GroundPoint {
  n: number;
  e: number;
}

export interface ScreenPoint {
  x: number;
  y: number;
}

export interface Size {
  width: number;
  height: number;
}

/** Space reserved inside a pane for overlaid controls, in pixels. */
export interface Insets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/** Fitted ground→pixel frame of a pane before the user's pan and zoom. */
export interface Frame {
  /** Pixels per ground unit at zoom 1. */
  scale: number;
  centerN: number;
  centerE: number;
  /** Pixel position of the frame center (the middle of the inset area). */
  originX: number;
  originY: number;
}

export interface Projection {
  pixelsPerUnit: number;
  toXY: (n: number, e: number) => ScreenPoint;
  toGround: (x: number, y: number) => GroundPoint;
}

/** Bounds of the finite points, or null when there are none. */
export const computeBounds = (points: Iterable<GroundPoint>): Bounds | null => {
  let minN = Infinity;
  let maxN = -Infinity;
  let minE = Infinity;
  let maxE = -Infinity;
  for (const { n, e } of points) {
    if (!Number.isFinite(n) || !Number.isFinite(e)) continue;
    if (n < minN) minN = n;
    if (n > maxN) maxN = n;
    if (e < minE) minE = e;
    if (e > maxE) maxE = e;
  }
  return minN === Infinity ? null : { minN, maxN, minE, maxE };
};

/**
 * Fit bounds into the pane inside its insets, leaving a relative margin
 * around the data. North and east share one scale so plotted geometry keeps
 * its true shape. A single point (no extent) is shown at a nominal scale.
 */
export const fitBounds = (
  bounds: Bounds,
  size: Size,
  insets: Insets,
  margin = 0.06
): Frame => {
  const availableWidth = Math.max(1, size.width - insets.left - insets.right);
  const availableHeight = Math.max(1, size.height - insets.top - insets.bottom);
  const spanE = (bounds.maxE - bounds.minE) * (1 + 2 * margin);
  const spanN = (bounds.maxN - bounds.minN) * (1 + 2 * margin);
  let scale = Math.min(
    spanE > 0 ? availableWidth / spanE : Infinity,
    spanN > 0 ? availableHeight / spanN : Infinity
  );
  if (!Number.isFinite(scale)) {
    // No extent: one ground unit spans most of the shorter side.
    scale = Math.min(availableWidth, availableHeight) / (1 + 2 * margin);
  }
  return {
    scale,
    centerN: (bounds.maxN + bounds.minN) / 2,
    centerE: (bounds.maxE + bounds.minE) / 2,
    originX: insets.left + availableWidth / 2,
    originY: insets.top + availableHeight / 2,
  };
};

/** Ground↔pixel mapping for a fitted frame under the user's view. */
export const project = (frame: Frame, view: ViewState): Projection => {
  const k = frame.scale * view.zoom;
  return {
    pixelsPerUnit: k,
    toXY: (n, e) => ({
      x: frame.originX + (e - frame.centerE) * k + view.panX,
      y: frame.originY - (n - frame.centerN) * k + view.panY,
    }),
    toGround: (x, y) => ({
      e: frame.centerE + (x - frame.originX - view.panX) / k,
      n: frame.centerN - (y - frame.originY - view.panY) / k,
    }),
  };
};

export const clampZoom = (zoom: number): number =>
  Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));

/**
 * Zoom by a factor while keeping the ground point under `anchor` fixed on
 * screen. `origin` is the frame origin (the pixel position of the frame
 * center at zero pan).
 */
export const zoomAround = (
  view: ViewState,
  factor: number,
  anchor: ScreenPoint,
  origin: ScreenPoint
): ViewState => {
  const zoom = clampZoom(view.zoom * factor);
  const k = zoom / view.zoom;
  if (k === 1) return view;
  return {
    zoom,
    panX: (anchor.x - origin.x) * (1 - k) + view.panX * k,
    panY: (anchor.y - origin.y) * (1 - k) + view.panY * k,
  };
};

/** Round to a 1 / 2 / 5 × 10ⁿ "nice" value (scale bars and grid spacing). */
export const niceDistance = (raw: number): number => {
  if (!Number.isFinite(raw) || raw <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(raw)));
  const mantissa = raw / pow;
  const nice = mantissa >= 5 ? 5 : mantissa >= 2 ? 2 : 1;
  return nice * pow;
};

/** Distance label without floating-point noise (0.30000000000000004 → 0.3). */
export const formatDistance = (distance: number): string =>
  distance >= 1
    ? distance.toLocaleString('en-US')
    : String(Number(distance.toPrecision(6)));

/** Residual stretch factor relative to the map scale ("×12", "×1.5"). */
export const formatMultiplier = (multiplier: number): string =>
  multiplier >= 10
    ? `×${Math.round(multiplier).toLocaleString('en-US')}`
    : `×${multiplier.toFixed(1)}`;

const MAX_GRID_LINES = 120;

/**
 * Pixel positions of ground-aligned grid lines at a nice spacing of about
 * `targetPx` pixels, so the grid moves with the data when panning.
 */
export const gridLines = (
  projection: Projection,
  size: Size,
  targetPx = 64
): { xs: number[]; ys: number[]; step: number } => {
  const step = niceDistance(targetPx / projection.pixelsPerUnit);
  const topLeft = projection.toGround(0, 0);
  const bottomRight = projection.toGround(size.width, size.height);
  const firstE = Math.ceil(topLeft.e / step);
  const lastE = Math.floor(bottomRight.e / step);
  const firstN = Math.ceil(bottomRight.n / step);
  const lastN = Math.floor(topLeft.n / step);
  if (
    !Number.isFinite(firstE + lastE + firstN + lastN) ||
    lastE - firstE > MAX_GRID_LINES ||
    lastN - firstN > MAX_GRID_LINES
  ) {
    return { xs: [], ys: [], step };
  }
  const xs: number[] = [];
  const ys: number[] = [];
  for (let i = firstE; i <= lastE; i++) {
    xs.push(projection.toXY(0, i * step).x);
  }
  for (let i = firstN; i <= lastN; i++) {
    ys.push(projection.toXY(i * step, 0).y);
  }
  return { xs, ys, step };
};

/**
 * Pixels drawn per ground unit of residual. 'auto' draws the reference
 * (largest) residual about 60px long, independent of zoom.
 */
export const residualPixelsPerUnit = (
  exaggeration: Exaggeration,
  referenceResidual: number,
  pixelsPerUnit: number,
  size: Size
): number =>
  exaggeration === 'auto'
    ? Math.max(
        12,
        Math.min(60, (Math.min(size.width, size.height) - 100) / 4)
      ) / Math.max(referenceResidual, 1e-9)
    : pixelsPerUnit * exaggeration;

/** Largest numeric residual of a fit (1 when there is none to scale by). */
export const largestResidual = (
  residuals: ReadonlyArray<{ total: number | null }>
): number => {
  const largest = Math.max(
    0,
    ...residuals.flatMap((residual) =>
      residual.total === null ? [] : [residual.total]
    )
  );
  return largest > 1e-9 ? largest : 1;
};

/** Largest fit movement (ground units), or null when nothing moved in view. */
export const largestMovement = (
  movements: ReadonlyArray<{
    previous: GroundPoint;
    current: GroundPoint | null;
  }>
): number | null => {
  let largest: number | null = null;
  for (const { previous, current } of movements) {
    if (!current) continue;
    const distance = Math.hypot(current.n - previous.n, current.e - previous.e);
    if (Number.isFinite(distance) && (largest === null || distance > largest)) {
      largest = distance;
    }
  }
  return largest;
};

/** Pane height for a given width: landscape, within readable limits. */
export const paneHeight = (width: number): number =>
  Math.round(Math.min(540, Math.max(280, width * 0.75)));
